// src/components/MarketInsights/components/PricingHistoryExplorer.tsx

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  PricingHistoryGroupBy,
  PricingHistoryMetric,
  PricingHistoryPoint,
  PricingHistoryResponse,
  PropertyListItem,
} from "../types";
import {
  formatCurrency,
  formatNumber,
  formatPricePerSqft,
} from "../utils/formatters";

interface PricingHistoryExplorerProps {
  data: PricingHistoryResponse | undefined;
  isLoading: boolean;
  isDisabled?: boolean;
  groupBy: PricingHistoryGroupBy;
  onGroupByChange: (groupBy: PricingHistoryGroupBy) => void;
  daysBack: number;
  onDaysBackChange: (daysBack: number) => void;
  metric: PricingHistoryMetric;
  onMetricChange: (metric: PricingHistoryMetric) => void;
  selectedProperties: PropertyListItem[];
  onClearProperty: (propertyId: string) => void;
}

const groupByOptions: { value: PricingHistoryGroupBy; label: string }[] = [
  { value: "bedrooms", label: "Bedrooms" },
  { value: "property", label: "Property" },
  { value: "floor_plan", label: "Floor Plans" },
  { value: "sqft_range", label: "Sq Ft" },
];

const metricOptions: { value: PricingHistoryMetric; label: string }[] = [
  { value: "avg_price", label: "Avg Rent" },
  { value: "min_price", label: "Min" },
  { value: "max_price", label: "Max" },
  { value: "avg_price_per_sqft", label: "$/Sq Ft" },
];

const dayOptions = [30, 90, 180, 365];
const MAX_FLOOR_PLAN_SERIES = 5;
const MAX_DEFAULT_SERIES = 6;

function formatHistoryDate(dateStr: string): string {
  const date = new Date(`${dateStr}T00:00:00`);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function formatMetricValue(
  value: number | null | undefined,
  metric: PricingHistoryMetric
): string {
  if (metric === "avg_price_per_sqft") {
    return formatPricePerSqft(value);
  }
  return formatCurrency(value);
}

function formatAxisValue(value: number, metric: PricingHistoryMetric): string {
  if (metric === "avg_price_per_sqft") {
    return `$${value.toFixed(2)}`;
  }
  return formatCurrency(value, { compact: true });
}

const PricingHistoryExplorer: React.FC<PricingHistoryExplorerProps> = ({
  data,
  isLoading,
  isDisabled = false,
  groupBy,
  onGroupByChange,
  daysBack,
  onDaysBackChange,
  metric,
  onMetricChange,
  selectedProperties,
  onClearProperty,
}) => {
  const theme = useTheme();

  const series = data?.series || [];
  const maxDefaultSeries =
    groupBy === "floor_plan" && selectedProperties.length === 1
      ? MAX_FLOOR_PLAN_SERIES
      : MAX_DEFAULT_SERIES;
  const seriesSignature = series.map((item) => item.id).join("|");
  const defaultVisibleSeriesIds = useMemo(
    () => series.slice(0, maxDefaultSeries).map((item) => item.id),
    [maxDefaultSeries, seriesSignature]
  );
  const [visibleSeriesIds, setVisibleSeriesIds] = useState<string[]>([]);
  const colors = [
    theme.palette.primary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
    theme.palette.info.main,
    theme.palette.secondary.main,
    theme.palette.error.main,
    theme.palette.primary.dark,
    theme.palette.success.dark,
    theme.palette.warning.dark,
    theme.palette.info.dark,
  ];
  const seriesColorMap = useMemo(() => {
    const colorMap = new Map<string, string>();
    series.forEach((item, index) => {
      colorMap.set(item.id, colors[index % colors.length]);
    });
    return colorMap;
  }, [colors, seriesSignature]);
  const visibleSeriesIdSet = useMemo(
    () => new Set(visibleSeriesIds),
    [visibleSeriesIds]
  );
  const displaySeries = series.filter((item) => visibleSeriesIdSet.has(item.id));

  useEffect(() => {
    setVisibleSeriesIds(defaultVisibleSeriesIds);
  }, [defaultVisibleSeriesIds]);

  const toggleSeries = (seriesId: string) => {
    setVisibleSeriesIds((prev) => {
      if (prev.includes(seriesId)) {
        if (prev.length === 1) return prev;
        return prev.filter((id) => id !== seriesId);
      }
      return [...prev, seriesId];
    });
  };

  const showAllSeries = () => {
    setVisibleSeriesIds(series.map((item) => item.id));
  };

  const resetVisibleSeries = () => {
    setVisibleSeriesIds(defaultVisibleSeriesIds);
  };

  const { chartData, seriesKeys } = useMemo(() => {
    const dateMap = new Map<string, Record<string, any>>();

    displaySeries.forEach((item, index) => {
      const key = `series_${index}`;
      item.points.forEach((point) => {
        const existing = dateMap.get(point.date) || {
          date: point.date,
          dateLabel: formatHistoryDate(point.date),
        };
        existing[key] = point[metric];
        existing[`${key}_point`] = point;
        dateMap.set(point.date, existing);
      });
    });

    return {
      chartData: Array.from(dateMap.values()).sort((a, b) =>
        String(a.date).localeCompare(String(b.date))
      ),
      seriesKeys: displaySeries.map((_, index) => `series_${index}`),
    };
  }, [displaySeries, metric]);

  const latestPoints = displaySeries
    .map((item) => item.points[item.points.length - 1])
    .filter(Boolean);
  const firstPoints = displaySeries
    .map((item) => item.points[0])
    .filter(Boolean);
  const latestWeightedAverage = latestPoints.length
    ? latestPoints.reduce(
        (sum, point) => sum + (point.avg_price || 0) * point.unit_count,
        0
      ) /
      (latestPoints.reduce((sum, point) => sum + point.unit_count, 0) || 1)
    : null;
  const firstWeightedAverage = firstPoints.length
    ? firstPoints.reduce(
        (sum, point) => sum + (point.avg_price || 0) * point.unit_count,
        0
      ) /
      (firstPoints.reduce((sum, point) => sum + point.unit_count, 0) || 1)
    : null;
  const rentTrend =
    latestWeightedAverage !== null && firstWeightedAverage !== null
      ? latestWeightedAverage - firstWeightedAverage
      : null;
  const observationCount = displaySeries.reduce(
    (sum, item) => sum + item.points.reduce((pointSum, point) => pointSum + point.unit_count, 0),
    0
  );
  const contextLabel =
    selectedProperties.length === 0
      ? "Market baseline"
      : selectedProperties.length === 1
        ? "Floor plan view"
        : "Property comparison";
  const subtitle =
    selectedProperties.length === 0
      ? "Market rent movement by bedroom"
      : selectedProperties.length === 1
        ? `Floor-plan rent movement for ${selectedProperties[0].name}`
        : "Average rent movement for selected properties";
  const trendRows = displaySeries
    .map((item) => {
      const firstPoint = item.points[0];
      const latestPoint = item.points[item.points.length - 1];
      const change =
        firstPoint?.avg_price !== null &&
        firstPoint?.avg_price !== undefined &&
        latestPoint?.avg_price !== null &&
        latestPoint?.avg_price !== undefined
          ? latestPoint.avg_price - firstPoint.avg_price
          : null;
      const changePct =
        change !== null && firstPoint?.avg_price
          ? (change / firstPoint.avg_price) * 100
          : null;
      return { item, latestPoint, change, changePct };
    })
    .filter((row) => row.latestPoint)
    .slice(0, 6);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const rows = payload.filter((entry: any) => entry.value !== null && entry.value !== undefined);
    if (!rows.length) return null;

    return (
      <Box
        sx={{
          backgroundColor: "background.paper",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: 1.5,
          boxShadow: theme.shadows[4],
          minWidth: 240,
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {rows[0]?.payload?.dateLabel}
        </Typography>
        <Box sx={{ mt: 1, display: "grid", gap: 0.75 }}>
          {rows.slice(0, 8).map((entry: any) => {
            const key = entry.dataKey as string;
            const index = seriesKeys.indexOf(key);
            const item = displaySeries[index];
            const point = entry.payload[`${key}_point`] as
              | PricingHistoryPoint
              | undefined;

            return (
              <Box key={key}>
                <Typography
                  variant="body2"
                  sx={{ color: entry.color, fontWeight: 600 }}
                >
                  {item?.label || entry.name}: {formatMetricValue(entry.value, metric)}
                </Typography>
                {point && (
                  <Typography variant="caption" color="text.secondary">
                    {formatNumber(point.unit_count)} observations ·{" "}
                    {formatNumber(point.available_count)} available
                  </Typography>
                )}
              </Box>
            );
          })}
          {rows.length > 8 && (
            <Typography variant="caption" color="text.secondary">
              +{rows.length - 8} more series
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton width="42%" height={32} />
          <Skeleton
            variant="rectangular"
            height={360}
            sx={{ mt: 2, borderRadius: 1 }}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" },
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={600}>
              Rent Trend Explorer
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1.5,
              justifyContent: { xs: "flex-start", lg: "flex-end" },
            }}
          >
            <ToggleButtonGroup
              value={groupBy}
              exclusive
              onChange={(_, value) => value && onGroupByChange(value)}
              size="small"
            >
              {groupByOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  disabled={
                    option.value === "floor_plan" &&
                    selectedProperties.length === 0
                  }
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={metric}
              exclusive
              onChange={(_, value) => value && onMetricChange(value)}
              size="small"
            >
              {metricOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <ToggleButtonGroup
              value={daysBack}
              exclusive
              onChange={(_, value) => value && onDaysBackChange(value)}
              size="small"
            >
              {dayOptions.map((days) => (
                <ToggleButton key={days} value={days}>
                  {days}d
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          <Chip
            label={contextLabel}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
          {selectedProperties.length > 0 ? (
            selectedProperties.map((property) => (
              <Chip
                key={property.id}
                label={property.name}
                onDelete={() => onClearProperty(property.id)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))
          ) : (
            <Chip label="All active properties" size="small" variant="outlined" />
          )}
        </Box>

        {series.length > 0 && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, minWidth: 0 }}>
              {series.map((item) => {
                const isVisible = visibleSeriesIdSet.has(item.id);
                const color = seriesColorMap.get(item.id) || theme.palette.primary.main;
                const latestPoint = item.points[item.points.length - 1];

                return (
                  <Chip
                    key={item.id}
                    clickable
                    onClick={() => toggleSeries(item.id)}
                    label={`${item.label} · ${formatCurrency(latestPoint?.avg_price)}`}
                    variant={isVisible ? "filled" : "outlined"}
                    sx={{
                      maxWidth: { xs: "100%", sm: 320 },
                      borderColor: alpha(color, isVisible ? 0.75 : 0.45),
                      color: isVisible ? color : "text.secondary",
                      backgroundColor: isVisible ? alpha(color, 0.12) : "transparent",
                      fontWeight: isVisible ? 700 : 500,
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                      "&:hover": {
                        backgroundColor: alpha(color, isVisible ? 0.18 : 0.08),
                      },
                    }}
                  />
                );
              })}
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={resetVisibleSeries}
                disabled={visibleSeriesIds.join("|") === defaultVisibleSeriesIds.join("|")}
              >
                Focus
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={showAllSeries}
                disabled={visibleSeriesIds.length === series.length}
              >
                All
              </Button>
            </Box>
          </Box>
        )}

        {chartData.length === 0 || displaySeries.length === 0 ? (
          <Box
            sx={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `1px dashed ${alpha(theme.palette.divider, 0.8)}`,
              borderRadius: 1,
            }}
          >
            <Typography color="text.secondary">
              {isDisabled
                ? "Select a property below to load pricing history."
                : "No historical pricing observations match the current filters"}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 360,
              "& .recharts-wrapper:focus, & .recharts-surface:focus": {
                outline: "none",
              },
            }}
          >
            <ResponsiveContainer>
              <LineChart
                data={chartData}
                margin={{ top: 12, right: 28, left: 8, bottom: 28 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(theme.palette.divider, 0.45)}
                />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={{ stroke: theme.palette.divider }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  tickLine={false}
                  axisLine={{ stroke: theme.palette.divider }}
                  tickFormatter={(value) => formatAxisValue(Number(value), metric)}
                  domain={
                    metric === "avg_price_per_sqft"
                      ? ["auto", "auto"]
                      : [
                          (dataMin: number) =>
                            Math.max(
                              0,
                              Math.floor((Number(dataMin) * 0.9) / 100) * 100
                            ),
                          (dataMax: number) =>
                            Math.ceil((Number(dataMax) * 1.08) / 100) * 100,
                        ]
                  }
                  width={72}
                />
                <Tooltip content={<CustomTooltip />} />
                {displaySeries.map((item, index) => (
                  <Line
                    key={item.id}
                    type="monotone"
                    dataKey={`series_${index}`}
                    name={item.label}
                    stroke={seriesColorMap.get(item.id) || colors[index % colors.length]}
                    strokeWidth={2.5}
                    dot={{ r: 2 }}
                    activeDot={{ r: 5 }}
                    connectNulls
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}

        {trendRows.length > 0 && (
          <Box
            sx={{
              mt: 2,
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
              },
              gap: 1,
            }}
          >
            {trendRows.map(({ item, latestPoint, change, changePct }, index) => (
              <Box
                key={item.id}
                sx={{
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                  borderRadius: 1,
                  px: 1.25,
                  py: 1,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  noWrap
                  sx={{
                    color:
                      seriesColorMap.get(item.id) ||
                      colors[index % colors.length],
                  }}
                >
                  {item.label}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Latest {formatCurrency(latestPoint.avg_price)}
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={700}
                    color={
                      change === null || change === 0
                        ? "text.secondary"
                        : change > 0
                          ? "error.main"
                          : "success.main"
                    }
                  >
                    {change === null
                      ? "No data yet"
                      : `${change > 0 ? "+" : ""}${formatCurrency(change)}${
                          changePct !== null
                            ? ` (${changePct > 0 ? "+" : ""}${changePct.toFixed(1)}%)`
                            : ""
                        }`}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Visible Series
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatNumber(displaySeries.length)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Observations
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatNumber(observationCount)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Latest Avg Rent
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {formatCurrency(latestWeightedAverage)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Rent Trend
            </Typography>
            <Typography
              variant="h6"
              fontWeight={600}
              color={
                rentTrend === null || rentTrend === 0
                  ? "text.primary"
                  : rentTrend > 0
                    ? "error.main"
                    : "success.main"
              }
            >
              {rentTrend === null
                ? "No data yet"
                : `${rentTrend > 0 ? "+" : ""}${formatCurrency(rentTrend)}`}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Date Range
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {data?.date_range?.start && data?.date_range?.end
                ? `${formatHistoryDate(data.date_range.start)} - ${formatHistoryDate(
                    data.date_range.end
                  )}`
                : "No data yet"}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default PricingHistoryExplorer;
